import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { relativeTime } from "@/lib/relativeTime";
import { BookOpen, HandHeart, Users, Sparkles, ChevronRight, Check, Flame, Clock, Music, Calendar, UserPlus } from "lucide-react";

export default function Dashboard() {
    const { t } = useTranslation();
    const { user } = useAuth();
    const [mine, setMine] = useState([]);
    const [today, setToday] = useState(null);
    const [psalm, setPsalm] = useState(null);
    const [notifs, setNotifs] = useState([]);
    const [toPray, setToPray] = useState([]);
    const [myGroups, setMyGroups] = useState([]);
    const [committed, setCommitted] = useState([]);
    const [following, setFollowing] = useState([]);
    const [prayingId, setPrayingId] = useState(null);

    useEffect(() => {
        api.get("/intentions/mine").then(({ data }) => setMine(data)).catch(() => {});
        api.get("/liturgical/today").then(({ data }) => setToday(data)).catch(() => {});
        api.get("/psalms/today").then(({ data }) => setPsalm(data)).catch(() => {});
        api.get("/notifications").then(({ data }) => setNotifs(data)).catch(() => {});
        api.get("/me/group-prayers").then(({ data }) => setMyGroups(data)).catch(() => {});
        api.get("/me/committed-intentions").then(({ data }) => setCommitted(data)).catch(() => {});
        api.get("/me/following").then(({ data }) => setFollowing(data)).catch(() => {});
        if (user?.is_warrior) {
            api.get("/intentions/to-pray").then(({ data }) => setToPray(data)).catch(() => {});
        }
    }, [user?.is_warrior]);

    const markFulfilled = async (id) => {
        await api.post(`/intentions/${id}/fulfill`);
        setMine((m) => m.map((x) => (x.id === id ? { ...x, status: "fulfilled" } : x)));
    };

    const prayFor = async (id) => {
        setPrayingId(id);
        try {
            await api.post(`/intentions/${id}/pray`);
            setToPray((prev) => prev.filter((x) => x.id !== id));
            // refresh committed list
            api.get("/me/committed-intentions").then(({ data }) => setCommitted(data)).catch(() => {});
        } finally {
            setPrayingId(null);
        }
    };

    const fmtDateTime = (iso) => {
        if (!iso) return "";
        try {
            const d = new Date(iso);
            return d.toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
        } catch { return iso; }
    };

    return (
        <div className="parchment min-h-[calc(100vh-4rem)]">
            <div className="mx-auto max-w-7xl px-6 md:px-10 py-12">
                <div className="flex items-end justify-between flex-wrap gap-4">
                    <div>
                        <div className="text-xs tracking-[0.28em] uppercase" style={{ color: "#7F1D1D" }}>Your sanctuary</div>
                        <h1 className="font-display text-4xl sm:text-5xl mt-2" style={{ color: "#1E1B4B" }}>
                            Peace be with you, {user?.name?.split(" ")[0] || "friend"}.
                        </h1>
                        {user?.is_warrior && (
                            <div className="mt-2 inline-flex items-center gap-2 text-sm" style={{ color: "#7F1D1D" }}>
                                <Sparkles size={14} /> You are a Prayer Warrior — preferred hour {user.prayer_time || "—"}
                            </div>
                        )}
                        {following.length > 0 && (
                            <div className="mt-1 inline-flex items-center gap-2 text-xs" style={{ color: "#525252" }} data-testid="dashboard-following-count">
                                <UserPlus size={12} /> Following {following.length} warrior{following.length === 1 ? "" : "s"}
                            </div>
                        )}
                    </div>
                    <div className="flex flex-wrap gap-3">
                        <Link to="/intentions" className="btn-primary text-sm" data-testid="dashboard-ask-prayer-link">Ask for Prayer</Link>
                        {!user?.is_warrior && <Link to="/join-warriors" className="btn-gold text-sm" data-testid="dashboard-join-warriors-link">Become a Warrior</Link>}
                    </div>
                </div>

                <div className="mt-10 grid lg:grid-cols-12 gap-6">
                    <div className="lg:col-span-8 space-y-6">
                        {user?.is_warrior && (
                            <div className="p-card p-6" data-testid="dashboard-to-pray">
                                <div className="flex items-center justify-between flex-wrap gap-2">
                                    <div className="flex items-center gap-2">
                                        <Flame size={16} style={{ color: "#D4AF37" }} />
                                        <div>
                                            <div className="text-xs tracking-[0.22em] uppercase" style={{ color: "#7F1D1D" }}>{t("dash.to_pray_h")}</div>
                                            <div className="font-display text-2xl mt-1" style={{ color: "#1E1B4B" }}>{t("dash.to_pray_sub")}</div>
                                        </div>
                                    </div>
                                    <Link to="/intentions" className="text-sm underline flex items-center gap-1" style={{ color: "#1E1B4B" }} data-testid="dashboard-all-open-link">
                                        Browse all <ChevronRight size={14} />
                                    </Link>
                                </div>
                                <div className="mt-4 divide-y" style={{ borderColor: "#E5E0D8" }}>
                                    {toPray.length === 0 ? (
                                        <div className="py-6 text-sm text-neutral-500">{t("dash.to_pray_empty")}</div>
                                    ) : toPray.map((i) => (
                                        <div key={i.id} className="py-3 flex items-start gap-3" data-testid={`dashboard-topray-${i.id}`}>
                                            {i.priority ? (
                                                <span className="text-[10px] tracking-[0.2em] uppercase mt-1 px-2 py-0.5 rounded-full flex-shrink-0"
                                                      style={{ background: "#1E1B4B", color: "#D4AF37" }}>Priority</span>
                                            ) : (
                                                <span className="text-[10px] tracking-[0.2em] uppercase mt-1 px-2 py-0.5 rounded-full flex-shrink-0"
                                                      style={{ background: "#F5F0E6", color: "#7F1D1D" }}>{i.category}</span>
                                            )}
                                            <div className="flex-1 min-w-0">
                                                <div className="font-display text-lg" style={{ color: "#1E1B4B" }}>{i.title}</div>
                                                {i.description && <div className="text-xs mt-0.5 line-clamp-1" style={{ color: "#525252" }}>{i.description}</div>}
                                                <div className="text-xs mt-1 flex items-center gap-2 flex-wrap" style={{ color: "#7F1D1D" }}>
                                                    <span className="inline-flex items-center gap-1"><HandHeart size={11} /> {i.prayer_count || 0} committed</span>
                                                    <span>·</span>
                                                    <span>asked by {i.requester_name}</span>
                                                    <span>·</span>
                                                    <span className="inline-flex items-center gap-1" style={{ color: "#525252" }}><Clock size={11} /> {relativeTime(i.created_at)}</span>
                                                </div>
                                            </div>
                                            <button onClick={() => prayFor(i.id)} disabled={prayingId === i.id}
                                                    className="btn-primary text-xs inline-flex items-center gap-1 disabled:opacity-60 flex-shrink-0"
                                                    data-testid={`dashboard-topray-btn-${i.id}`}>
                                                <HandHeart size={12} /> {prayingId === i.id ? "…" : t("dash.pray_btn")}
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="p-card p-6">
                            <div className="flex items-center justify-between flex-wrap gap-2">
                                <div>
                                    <div className="text-xs tracking-[0.22em] uppercase" style={{ color: "#7F1D1D" }}>My Intentions</div>
                                    <div className="font-display text-2xl mt-1" style={{ color: "#1E1B4B" }}>Carried in prayer</div>
                                </div>
                                <Link to="/intentions" className="text-sm underline flex items-center gap-1" style={{ color: "#1E1B4B" }} data-testid="dashboard-all-intentions-link">
                                    New intention <ChevronRight size={14} />
                                </Link>
                            </div>
                            <div className="mt-4 divide-y" style={{ borderColor: "#E5E0D8" }}>
                                {mine.length === 0 && <div className="py-6 text-sm text-neutral-500">You haven’t shared any intentions yet.</div>}
                                {mine.map((i) => (
                                    <div key={i.id} className="py-4 flex items-start gap-4" data-testid={`dashboard-mine-${i.id}`}>
                                        <div className="h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0"
                                             style={{ background: "#F5F0E6", color: "#7F1D1D" }}>
                                            <HandHeart size={16} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="font-display text-lg" style={{ color: "#1E1B4B" }}>{i.title}</div>
                                            {i.description && <div className="text-sm text-neutral-600 line-clamp-2">{i.description}</div>}
                                            <div className="mt-1.5 text-xs flex items-center gap-2 flex-wrap" style={{ color: "#525252" }}>
                                                <span>{i.prayer_count} warriors praying</span>
                                                <span>·</span>
                                                <span>{i.status}</span>
                                                <span>·</span>
                                                <span className="inline-flex items-center gap-1"><Clock size={11} /> {relativeTime(i.created_at)}</span>
                                            </div>
                                        </div>
                                        {i.status === "active" ? (
                                            <button onClick={() => markFulfilled(i.id)} className="text-xs px-3 py-1.5 rounded-full border flex items-center gap-1"
                                                    style={{ borderColor: "#D4AF37", color: "#1E1B4B" }} data-testid={`dashboard-fulfill-${i.id}`}>
                                                <Check size={12} /> Fulfilled
                                            </button>
                                        ) : (
                                            <span className="text-xs px-3 py-1 rounded-full" style={{ background: "#1E1B4B", color: "#FDFBF7" }}>Thanksgiving</span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="p-card p-6" data-testid="dashboard-committed">
                            <div className="flex items-center justify-between flex-wrap gap-2">
                                <div>
                                    <div className="text-xs tracking-[0.22em] uppercase" style={{ color: "#7F1D1D" }}>Intentions entrusted to you</div>
                                    <div className="font-display text-2xl mt-1" style={{ color: "#1E1B4B" }}>You committed to pray</div>
                                </div>
                                <Link to="/intentions" className="text-sm underline flex items-center gap-1" style={{ color: "#1E1B4B" }}>
                                    Browse all <ChevronRight size={14} />
                                </Link>
                            </div>
                            <div className="mt-4 divide-y" style={{ borderColor: "#E5E0D8" }}>
                                {committed.length === 0 ? (
                                    <div className="py-6 text-sm text-neutral-500">You haven’t committed to any intention yet. Visit the board and press “I’m praying”.</div>
                                ) : committed.slice(0, 8).map((i) => (
                                    <div key={i.id} className="py-3" data-testid={`dashboard-committed-${i.id}`}>
                                        <div className="flex items-start gap-3">
                                            <span className="text-[10px] tracking-[0.2em] uppercase mt-1 px-2 py-0.5 rounded-full flex-shrink-0"
                                                  style={{ background: "#F5F0E6", color: "#7F1D1D" }}>{i.category}</span>
                                            <div className="flex-1 min-w-0">
                                                <div className="font-display text-base" style={{ color: "#1E1B4B" }}>{i.title}</div>
                                                <div className="text-xs mt-0.5 flex items-center gap-2 flex-wrap" style={{ color: "#525252" }}>
                                                    <span>asked by {i.requester_name}</span>
                                                    <span>·</span>
                                                    <span className="inline-flex items-center gap-1"><Clock size={11} /> prayed {relativeTime(i.committed_at)}</span>
                                                </div>
                                            </div>
                                            {i.status === "fulfilled" && (
                                                <span className="text-[10px] px-2 py-1 rounded-full" style={{ background: "#1E1B4B", color: "#D4AF37" }}>Fulfilled</span>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="p-card p-6">
                            <div className="text-xs tracking-[0.22em] uppercase" style={{ color: "#7F1D1D" }}>Messages from the centre</div>
                            <div className="font-display text-2xl mt-1" style={{ color: "#1E1B4B" }}>Recent notifications</div>
                            <div className="mt-4 divide-y" style={{ borderColor: "#E5E0D8" }}>
                                {notifs.length === 0 && <div className="py-6 text-sm text-neutral-500">Nothing yet. You’ll be notified when someone asks for prayers.</div>}
                                {notifs.slice(0, 8).map((n) => (
                                    <div key={n.id} className="py-3" data-testid={`dashboard-notif-${n.id}`}>
                                        <div className="text-sm font-semibold" style={{ color: "#1E1B4B" }}>{n.title}</div>
                                        <div className="text-xs text-neutral-600">{n.body}</div>
                                        <div className="text-[11px] mt-0.5 inline-flex items-center gap-1" style={{ color: "#8a8a8a" }}>
                                            <Clock size={10} /> {relativeTime(n.created_at)}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="lg:col-span-4 space-y-6">
                        {today && today.date && (
                            <div className="p-card p-6" data-testid="dashboard-today-reading">
                                <div className="flex items-center gap-2">
                                    <BookOpen size={14} style={{ color: "#7F1D1D" }} />
                                    <div className="text-xs tracking-[0.22em] uppercase" style={{ color: "#7F1D1D" }}>Today’s reading</div>
                                </div>
                                <div className="font-display text-xl mt-2" style={{ color: "#1E1B4B" }}>{today.feast || today.date}</div>
                                {today.word_of_god && <div className="text-sm mt-3 italic" style={{ color: "#525252" }}>{today.word_of_god}</div>}
                                <Link to="/liturgical" className="mt-4 inline-flex text-sm underline" style={{ color: "#7F1D1D" }} data-testid="dashboard-open-liturgical-link">
                                    Read today’s liturgy
                                </Link>
                            </div>
                        )}

                        {psalm && (
                            <div className="p-card p-6" data-testid="dashboard-psalm-today">
                                <div className="flex items-center gap-2">
                                    <Music size={14} style={{ color: "#7F1D1D" }} />
                                    <div className="text-xs tracking-[0.22em] uppercase" style={{ color: "#7F1D1D" }}>Psalm of the day</div>
                                </div>
                                <div className="font-display text-xl mt-2" style={{ color: "#1E1B4B" }}>Psalm {psalm.n}{psalm.title ? ` — ${psalm.title}` : ""}</div>
                                {psalm.verse && <div className="text-sm mt-3 italic" style={{ color: "#525252" }}>“{psalm.verse}”</div>}
                                <Link to={`/psalms`} className="mt-4 inline-flex text-sm underline" style={{ color: "#7F1D1D" }} data-testid="dashboard-open-psalms-link">
                                    Open the Psalter
                                </Link>
                            </div>
                        )}

                        <div className="p-card p-6" data-testid="dashboard-my-groups">
                            <div className="flex items-center gap-2">
                                <Calendar size={14} style={{ color: "#7F1D1D" }} />
                                <div className="text-xs tracking-[0.22em] uppercase" style={{ color: "#7F1D1D" }}>My group prayers</div>
                            </div>
                            <div className="mt-3 space-y-3">
                                {myGroups.length === 0 ? (
                                    <div className="text-sm text-neutral-500">You haven’t joined a group prayer yet.</div>
                                ) : myGroups.slice(0, 5).map((g) => (
                                    <div key={g.id} className="flex items-start gap-2" data-testid={`dashboard-mygroup-${g.id}`}>
                                        <div className="h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0"
                                             style={{ background: "#1E1B4B", color: "#D4AF37" }}>
                                            <Users size={14} />
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <div className="font-display text-base leading-tight" style={{ color: "#1E1B4B" }}>{g.title}</div>
                                            <div className="text-xs" style={{ color: "#525252" }}>{fmtDateTime(g.scheduled_at)} · {g.duration_min} min</div>
                                            {g.host_id === user?.id && <div className="text-[10px] uppercase tracking-wider mt-0.5" style={{ color: "#7F1D1D" }}>Host</div>}
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <Link to="/group-prayers" className="mt-4 inline-flex text-sm underline" style={{ color: "#7F1D1D" }} data-testid="dashboard-group-prayers-link">
                                Find more group prayers
                            </Link>
                        </div>

                        <div className="p-card p-6">
                            <Users size={14} style={{ color: "#7F1D1D" }} />
                            <div className="font-display text-xl mt-2" style={{ color: "#1E1B4B" }}>Quick actions</div>
                            <div className="mt-3 flex flex-col gap-2">
                                <Link to="/special" className="btn-ghost text-sm text-left" data-testid="dashboard-special-link">Submit a special request</Link>
                                <Link to="/group-prayers" className="btn-ghost text-sm text-left" data-testid="dashboard-group-link">Find a group prayer</Link>
                                <Link to="/testimonies" className="btn-ghost text-sm text-left" data-testid="dashboard-testimonies-link">Share a testimony</Link>
                                <Link to="/profile" className="btn-ghost text-sm text-left" data-testid="dashboard-profile-link">Update your profile photo</Link>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
